import { onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { FOLLOW_ZOOM, GEOLOCATION_OPTIONS, DEBOUNCE_DELAY } from '../../constants';
import { isValidCoordinates } from '../../utils/security';

export const useGeolocationLayer = ({ map, mapCenter, tilt }) => {
    const marker = ref(null);
    const coordinates = reactive({ lat: null, lng: null, heading: null, accuracy: null });
    const permissionStatus = ref(null);
    const geolocationError = ref(null);
    let positionWatcherId = null;
    let debounceTimer = null;
    let AdvancedMarkerElement = null;

    const updatePermissionStatus = async () => {
        try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            permissionStatus.value = permission.state;

            if (permission.state === 'granted' || permission.state === 'prompt') {
                startWatchingPosition();
            } else if (permission.state === 'denied') {
                geolocationError.value = 'Location access denied. Please enable location in your browser settings.';
            }

            permission.onchange = () => {
                permissionStatus.value = permission.state;
                if (permissionStatus.value === 'granted') {
                    geolocationError.value = null;
                    startWatchingPosition();
                } else {
                    stopWatchingPosition();
                }
            };
        } catch (error) {
            geolocationError.value = `Failed to check geolocation permission: ${error.message}`;
            console.error('Error checking geolocation permission:', error);
        }
    };

    const startWatchingPosition = () => {
        if (!navigator.geolocation) {
            geolocationError.value = 'Geolocation is not supported in this browser.';
            return;
        }

        positionWatcherId = navigator.geolocation.watchPosition(
            (pos) => {
                if (!isValidCoordinates(pos.coords.latitude, pos.coords.longitude)) {
                    geolocationError.value = 'Invalid coordinates received.';
                    return;
                }

                Object.assign(coordinates, {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    heading: pos.coords.heading,
                    accuracy: pos.coords.accuracy
                });

                // Update marker with debounce
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    addOrUpdateMarkerPosition();
                }, DEBOUNCE_DELAY);
            },
            (error) => {
                let errorMessage = 'Failed to get location.';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location permission denied.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out.';
                        break;
                }
                geolocationError.value = errorMessage;
                console.error('Error getting position:', error);
            },
            GEOLOCATION_OPTIONS
        );
    };

    const stopWatchingPosition = () => {
        if (positionWatcherId !== null) {
            navigator.geolocation.clearWatch(positionWatcherId);
            positionWatcherId = null;
        }
    };

    const addMarker = () => {
        if (!map.value || !isValidCoordinates(coordinates.lat, coordinates.lng)) {
            return;
        }

        // Remove previous marker
        if (marker.value) {
            marker.value.setMap(null);
        }

        if (!AdvancedMarkerElement) {
            geolocationError.value = 'Advanced marker library not loaded.';
            return;
        }

        try {
            const element = document.createElement('div');
            const iconName = map.value.getZoom() === FOLLOW_ZOOM ? 'locationArrow' : 'circle';
            const baseUrl = import.meta.env.BASE_URL || '/';
            element.innerHTML = `
                <img src="${baseUrl}images/${iconName}.svg"
                    style="transform: rotate3d(0, 0, 1, 90deg) rotateY(${tilt.value}deg); width: 20px; height: 20px;"
                    alt="location marker"
                />
            `;

            marker.value = new AdvancedMarkerElement({
                position: { lat: coordinates.lat, lng: coordinates.lng },
                map: map.value,
                content: element,
            });
        } catch (error) {
            geolocationError.value = `Failed to create location marker: ${error.message}`;
            console.error('Error creating marker:', error);
        }
    };

    const addOrUpdateMarkerPosition = () => {
        if (!map.value || !isValidCoordinates(coordinates.lat, coordinates.lng)) {
            return;
        }

        if (marker.value) {
            try {
                marker.value.position = new google.maps.LatLng(coordinates.lat, coordinates.lng);

                if (map.value.getZoom() === FOLLOW_ZOOM) {
                    map.value.moveCamera({
                        heading: coordinates.heading,
                        center: new google.maps.LatLng(coordinates.lat, coordinates.lng),
                    });
                }
            } catch (error) {
                console.error('Error updating marker position:', error);
            }
        } else {
            addMarker();
        }
    };

    onMounted(async () => {
        try {
            const markerLib = await google.maps.importLibrary("marker");
            AdvancedMarkerElement = markerLib.AdvancedMarkerElement;

            if (!AdvancedMarkerElement) {
                geolocationError.value = 'Advanced marker element library failed to load.';
                return;
            }

            await updatePermissionStatus();
        } catch (err) {
            geolocationError.value = `Failed to import Google Maps marker library: ${err.message}`;
            console.error('Error importing Google Maps library:', err);
        }
    });

    watch([tilt], () => {
        if (marker.value) {
            addMarker(); // Update existing marker
        }
    });

    watch(permissionStatus, (status) => {
        if (status === 'granted') {
            startWatchingPosition();
        } else {
            stopWatchingPosition();
        }
    });

    onUnmounted(() => {
        stopWatchingPosition();
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        if (marker.value) {
            marker.value.setMap(null);
            marker.value = null;
        }
    });

    return {
        map,
        coordinates,
        permissionStatus,
        geolocationError,
        updatePermissionStatus,
    };
};
